import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useBerichtVersenden, usePruefungLoeschen, usePruefungen } from '../api/abfragen'
import { PRUEFARTTEXT } from '../api/typen'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, datumDeutsch } from '../bausteine/Hinweise'
import { useAmSchreibtisch } from '../bausteine/schreibtisch'
import { darf, useKonto } from '../konto/KontoKontext'

export function Pruefungen() {
  const alle = usePruefungen()
  const loeschen = usePruefungLoeschen()
  /* Löschen nur am Schreibtisch. Draußen, mit Handschuhen, auf einem Handy
     in der Jackentasche ist ein Fehlgriff zu leicht – und was gelöscht ist,
     ist weg. Das ist ein Schutz gegen Versehen; die belastbaren Grenzen
     (wer darf, und was darf) stehen im Server. */
  const schreibtisch = useAmSchreibtisch()
  const { konto } = useKonto()
  const darfVersenden = darf(konto, 'bericht_versenden')

  if (alle.isLoading) return <Ladeanzeige />
  if (alle.error) return <Fehlerhinweis fehler={alle.error} />

  const offen = (alle.data ?? []).filter((p) => p.status !== 'abgeschlossen')
  const fertig = (alle.data ?? []).filter((p) => p.status === 'abgeschlossen')

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">Prüfungen</h1>

      <Fehlerhinweis fehler={loeschen.error} />

      <section>
        <h2 className="ueberschrift-akzent mb-3 text-xl">In Arbeit ({offen.length})</h2>
        {offen.length === 0 ? (
          <Leerhinweis
            titel="Keine offene Prüfung"
            text="Eine neue Prüfung beginnt am Spielplatz."
          />
        ) : (
          <ul className="space-y-2">
            {offen.map((p) => (
              <li key={p.id} className="flex items-stretch gap-2">
                <Link to={`/pruefungen/${p.id}/bogen`} className="karte-knopf">
                  <div className="flex-1">
                    <div className="font-semibold">{p.spielplatz_name}</div>
                    <div className="text-base text-slate-700">
                      {p.kunde_name} · {PRUEFARTTEXT[p.pruefart]} · {datumDeutsch(p.datum)}
                    </div>
                  </div>
                  <span className="marke bg-marke-100 text-marke-700">
                    {p.status === 'entwurf' ? 'Entwurf' : 'läuft'}
                  </span>
                </Link>
                {schreibtisch && (
                  <button
                    type="button"
                    className="knopf-warnung shrink-0 px-3 text-base"
                    disabled={loeschen.isPending}
                    onClick={() => {
                      const frage =
                        `Prüfung „${p.spielplatz_name}" vom ` +
                        `${datumDeutsch(p.datum)} endgültig löschen?\n\n` +
                        'Alle Beurteilungen und Fotos dieser Prüfung gehen mit. ' +
                        'Das lässt sich nicht rückgängig machen.'
                      if (window.confirm(frage)) loeschen.mutate(p.id)
                    }}
                  >
                    Löschen
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="ueberschrift-akzent mb-3 text-xl">Abgeschlossen ({fertig.length})</h2>
        {fertig.length === 0 ? (
          <Leerhinweis titel="Noch kein Bericht abgeschlossen" />
        ) : (
          <ul className="space-y-2">
            {fertig.map((p) => (
              <li key={p.id} className="karte flex items-center gap-3">
                <div className="flex-1">
                  <div className="font-semibold">
                    {p.berichtsnummer} · {p.spielplatz_name}
                  </div>
                  <div className="text-base text-slate-700">
                    {p.kunde_name} · {PRUEFARTTEXT[p.pruefart]} · {datumDeutsch(p.datum)}
                  </div>
                </div>
                <Link to={`/pruefungen/${p.id}/bogen`} className="knopf-still px-3">
                  Ansehen
                </Link>
                <a
                  className="knopf-zweit px-3"
                  href={`/api/pruefungen/${p.id}/bericht.pdf`}
                  target="_blank"
                  rel="noreferrer"
                >
                  PDF
                </a>
                {darfVersenden && (
                  <Versandknopf
                    pruefungId={p.id}
                    beschriftung={`${p.berichtsnummer} · ${p.spielplatz_name}`}
                  />
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}

/**
 * Bericht an den Kunden schicken.
 *
 * Mit Rückfrage, und die Rückfrage nennt die Adresse: der Bericht geht an
 * eine Gemeindeverwaltung und lässt sich nicht zurückholen. Wer eine andere
 * Adresse braucht, trägt sie hier ein – der Kunde bleibt davon unberührt.
 */
function Versandknopf({
  pruefungId,
  beschriftung,
}: {
  pruefungId: string
  beschriftung: string
}) {
  const versenden = useBerichtVersenden()
  const [offen, setzeOffen] = useState(false)
  const [an, setzeAn] = useState('')

  if (versenden.data) {
    return (
      <span role="status" className="marke bg-stufe-ob-grund px-3 text-stufe-ob">
        {versenden.data.verschickt
          ? `An ${versenden.data.an}`
          : 'Vermerkt, nicht zugestellt'}
      </span>
    )
  }

  if (!offen) {
    return (
      <button
        type="button"
        className="knopf-still px-3"
        onClick={() => setzeOffen(true)}
      >
        Verschicken
      </button>
    )
  }

  return (
    <div className="w-full space-y-2 border-t-2 border-slate-200 pt-3">
      <p className="text-base text-slate-700">
        {beschriftung} als PDF an den Kunden schicken.
      </p>
      <label className="block">
        <span className="text-base text-slate-700">
          Andere Adresse (leer lassen für die aus der Kundenakte)
        </span>
        <input
          type="email"
          className="mt-1"
          value={an}
          placeholder="rathaus@gemeinde.de"
          onChange={(e) => setzeAn(e.target.value)}
        />
      </label>
      <Fehlerhinweis fehler={versenden.error} />
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="knopf-haupt"
          disabled={versenden.isPending}
          onClick={() => versenden.mutate({ id: pruefungId, an: an.trim() })}
        >
          {versenden.isPending ? 'Verschickt …' : 'Jetzt verschicken'}
        </button>
        <button
          type="button"
          className="knopf-still"
          onClick={() => {
            setzeOffen(false)
            versenden.reset()
          }}
        >
          Abbrechen
        </button>
      </div>
    </div>
  )
}
